import React, { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { Redirect } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'

import { isPermittedPermission } from './../../helpers'
import { ContentRoute } from '../router'

/**
 * Gate around a Route that respects the user's authentication +
 * permission state. The earlier version threw a PermissionDeniedError
 * that was caught by the global ErrorBoundary and replaced the whole
 * tree with <ForbiddenPage />. That worked while ForbiddenPage was a
 * static page, but the new ForbiddenPage uses notistack + react-router
 * hooks, and ErrorBoundary discards the SnackbarProvider / Router
 * context underneath when it renders an error UI — so the user ended
 * up on a blank screen instead of being redirected with a toast.
 *
 * We now handle both cases inline:
 *  - not authenticated  → <Redirect to="/auth/login" />
 *  - permission denied  → toast + <Redirect to="/" />
 * No throw, no ErrorBoundary fallback, no blank screen.
 */
const ProtectedRoute = ({
  isAuthenticated = false,
  anonymous = true,
  can,
  isSuperuser,
  permissions,
  ...props
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const intl = useIntl()
  const toastFired = useRef(false)

  const needsAuth = !isAuthenticated && !anonymous
  const lacksPermission =
    isAuthenticated && !isSuperuser && !isPermittedPermission(can, permissions)

  useEffect(() => {
    if (lacksPermission && !toastFired.current) {
      toastFired.current = true
      enqueueSnackbar(
        intl.formatMessage({
          id: "ERROR.FORBIDDEN.SUB_TITLE",
          defaultMessage: "Vous n'avez pas accès à cette page.",
        }),
        {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 4000,
        }
      )
    }
  }, [lacksPermission, enqueueSnackbar, intl])

  if (needsAuth) return <Redirect to="/auth/login" />
  if (lacksPermission) return <Redirect to="/" />

  return (<ContentRoute {...props} />)
}

const mapStateToProps = (state) => state.common.auth

export default connect(mapStateToProps)(ProtectedRoute)
